import { createWithEqualityFn } from 'zustand/traditional';
import { formatMac, normalizeMac } from '@shared/utils/mac';

export type MacEventAction = 'learned' | 'removed' | 'moved';

export interface MacTimelineEvent {
  id: string;
  macAddress: string;
  action: MacEventAction;
  deviceId?: number;
  deviceName?: string;
  portName?: string;
  portNumber?: number;
  vlanId?: number | null;
  source?: string;
  timestamp: string;
}

interface DeviceMacSearchState {
  macInput: string;
  submittedMac: string | null;
  timeline: MacTimelineEvent[];
  lastRealtimeAt: string | null;
  lastSnapshotAt: string | null;
  actionCounters: Record<MacEventAction, number>;
  setMacInput: (value: string) => void;
  submit: (value: string) => void;
  pushEvent: (event: MacTimelineEvent) => void;
  recordRealtimeEvent: (event: MacTimelineEvent) => void;
  updateSnapshotTimestamp: (timestamp: string | null) => void;
  reset: () => void;
}

const MAX_TIMELINE_ITEMS = 20;

const initialState = {
  macInput: '',
  submittedMac: null as string | null,
  timeline: [] as MacTimelineEvent[],
  lastRealtimeAt: null as string | null,
  lastSnapshotAt: null as string | null,
  actionCounters: {
    learned: 0,
    removed: 0,
    moved: 0,
  } as Record<MacEventAction, number>,
};

export const useDeviceMacSearchStore = createWithEqualityFn<DeviceMacSearchState>()((set, get) => ({
  ...initialState,
  setMacInput: (value) => set({ macInput: value }),
  submit: (value) =>
    set({
      submittedMac: formatMac(value),
      timeline: [],
      actionCounters: {
        learned: 0,
        removed: 0,
        moved: 0,
      },
      lastRealtimeAt: null,
      lastSnapshotAt: null,
    }),
  recordRealtimeEvent: (event) =>
    set((state) => ({
      ...state,
      lastRealtimeAt: event.timestamp ?? state.lastRealtimeAt ?? new Date().toISOString(),
      actionCounters: {
        ...state.actionCounters,
        [event.action]: (state.actionCounters[event.action] ?? 0) + 1,
      },
    })),
  pushEvent: (event) => {
    const formattedMac = formatMac(event.macAddress);
    const currentMac = get().submittedMac;

    set((state) => {
      if (!currentMac || formattedMac !== currentMac) {
        return state;
      }

      const existingIndex = state.timeline.findIndex((item) => item.id === event.id);
      const nextTimeline =
        existingIndex >= 0
          ? state.timeline.map((item, index) => (index === existingIndex ? { ...item, ...event } : item))
          : [event, ...state.timeline].slice(0, MAX_TIMELINE_ITEMS);

      return {
        ...state,
        timeline: nextTimeline,
        lastRealtimeAt: state.lastRealtimeAt ?? event.timestamp ?? new Date().toISOString(),
      };
    });
  },
  updateSnapshotTimestamp: (timestamp) =>
    set({
      lastSnapshotAt: timestamp,
    }),
  reset: () => set({ ...initialState }),
}));

export function normalizeMacInput(input: string): string {
  return formatMac(normalizeMac(input));
}
